import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { formatDistanceToNow } from 'date-fns';
import { Typography, useTheme } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';

import DataTable from '../common/DataTable';
import EntityCell from '../common/EntityCell';
import RowActions from '../common/RowActions';
import { listExecutions, retryExecution, cancelExecution } from '../../redux/actions/ingestion/executions';

const rel = (d) => { try { return d ? formatDistanceToNow(new Date(d), { addSuffix: true }) : '—'; } catch (_e) { return '—'; } };
const num = (n) => (typeof n === 'number' ? n.toLocaleString() : '—');
const dur = (s) => (s == null ? '—' : s < 60 ? `${s.toFixed(1)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`);
const STATUS_TONE = { completed: 'success', processing: 'info', queued: 'warning', failed: 'error', cancelled: 'neutral', partial: 'warning' };

const ConnectorExecutions = ({ connector, executions, isLoading, listExecutions, retryExecution, cancelExecution }) => {
  const theme = useTheme();
  const sourceId = connector?.source_id;

  useEffect(() => { if (sourceId) listExecutions({ sourceId, perPage: 50 }); }, [listExecutions, sourceId]);

  const rows = Array.isArray(executions) ? executions.filter((e) => !sourceId || e.source_id === sourceId) : [];

  const columns = [
    {
      key: 'identity', label: 'Run', width: 130,
      render: (e) => <EntityCell id={e.execution_id} tone={STATUS_TONE[e.status] || 'neutral'} label={e.status} sublabel={e.trigger_type} />,
    },
    {
      key: 'rows', label: 'Rows', width: 120,
      render: (e) => <Typography sx={{ fontSize: 13 }}>{num(e.stats?.rows_inserted ?? e.stats?.rows_processed)}</Typography>,
    },
    { key: 'duration', label: 'Duration', width: 100, render: (e) => <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{dur(e.stats?.duration_seconds ?? e.duration_seconds)}</Typography> },
    {
      key: 'throughput', label: 'Rate', width: 100,
      render: (e) => { const t = e.stats?.throughput_records_per_sec; return <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t ? `${Math.round(t)}/s` : '—'}</Typography>; },
    },
    { key: 'started', label: 'Started', width: 140, render: (e) => <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{rel(e.started_at)}</Typography> },
    {
      key: 'error', label: 'Detail',
      render: (e) => e.error?.message
        ? <Typography sx={{ fontSize: 12, color: theme.palette.error.main }} noWrap title={e.error.message}>{e.error.message}</Typography>
        : <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>—</Typography>,
    },
    {
      key: 'actions', label: '', width: 56, align: 'right', stopRowClick: true,
      render: (e) => {
        const items = [];
        if (['failed', 'cancelled', 'partial', 'completed'].includes(e.status)) items.push({ label: 'Retry', icon: ReplayIcon, onClick: () => retryExecution(e.execution_id) });
        if (['queued', 'processing'].includes(e.status)) items.push({ label: 'Cancel', icon: CloseIcon, danger: true, onClick: () => cancelExecution(e.execution_id) });
        return items.length ? <RowActions onOpen={null} items={items} /> : null;
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      loading={isLoading}
      getRowKey={(e) => e.execution_id}
      emptyState={{ icon: <HistoryIcon />, title: 'No runs yet', message: 'Sync runs will appear here once a job executes.' }}
    />
  );
};

const mapStateToProps = (state) => ({
  executions: state.ingestion_executions?.executions || [],
  isLoading: state.ingestion_executions?.isLoading || false,
});

export default connect(mapStateToProps, { listExecutions, retryExecution, cancelExecution })(ConnectorExecutions);
